/**
 * Auth Storage Utility
 *
 * Persists authentication data in localStorage so the session is shared across
 * tabs and windows (opening a protected URL in a new tab sees the same login).
 *
 * On read, migrates legacy values from sessionStorage so existing sessions survive
 * the switch. Clears both storages for auth keys on logout to avoid stale copies.
 */

const AUTH_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  USER_DATA: 'userData',
  PROFILE_IS_PROCESSED: 'profileIsProcessed',
};

const removeLegacySessionKey = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (_) {
    // ignore
  }
};

/**
 * Get an item from localStorage, migrating from sessionStorage if present.
 */
const getItem = (key) => {
  try {
    let value = localStorage.getItem(key);
    if (value === null) {
      const legacy = sessionStorage.getItem(key);
      if (legacy !== null) {
        localStorage.setItem(key, legacy);
        removeLegacySessionKey(key);
        value = legacy;
      }
    }
    return value;
  } catch (error) {
    console.warn(`Failed to get ${key} from localStorage:`, error);
    return null;
  }
};

/**
 * Set an item in localStorage (and drop any legacy sessionStorage copy).
 */
const setItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    removeLegacySessionKey(key);
  } catch (error) {
    console.error(`Failed to set ${key} in localStorage:`, error);
    throw error;
  }
};

/**
 * Remove an item from both localStorage and sessionStorage.
 */
const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
    removeLegacySessionKey(key);
  } catch (error) {
    console.warn(`Failed to remove ${key} from storage:`, error);
  }
};

/**
 * Clear all auth-related items from localStorage and sessionStorage (legacy).
 */
const clearAuthData = () => {
  const clearKey = (key) => {
    try {
      localStorage.removeItem(key);
      removeLegacySessionKey(key);
    } catch (_) {
      // ignore
    }
  };
  try {
    Object.values(AUTH_KEYS).forEach(clearKey);
  } catch (error) {
    console.warn('Failed to clear auth data from storage:', error);
    Object.values(AUTH_KEYS).forEach(clearKey);
  }
};

/**
 * Get the authentication token
 */
export const getToken = () => getItem(AUTH_KEYS.TOKEN);

/**
 * Set the authentication token
 */
export const setToken = (token) => setItem(AUTH_KEYS.TOKEN, token);

/**
 * Get the refresh token
 */
export const getRefreshToken = () => getItem(AUTH_KEYS.REFRESH_TOKEN);

/**
 * Set the refresh token
 */
export const setRefreshToken = (refreshToken) =>
  setItem(AUTH_KEYS.REFRESH_TOKEN, refreshToken);

/**
 * Get the user data
 */
export const getUser = () => {
  const userString = getItem(AUTH_KEYS.USER);
  if (!userString) return null;
  try {
    return JSON.parse(userString);
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
};

/**
 * Set the user data
 */
export const setUser = (userData) => {
  try {
    setItem(AUTH_KEYS.USER, JSON.stringify(userData));
  } catch (error) {
    console.error('Failed to set user data:', error);
    throw error;
  }
};

/**
 * Get profile processed status
 */
export const getProfileIsProcessed = () => {
  const value = getItem(AUTH_KEYS.PROFILE_IS_PROCESSED);
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value === 'true';
  }
};

/**
 * Remove authentication token
 */
export const removeToken = () => removeItem(AUTH_KEYS.TOKEN);

/**
 * Remove refresh token
 */
export const removeRefreshToken = () => removeItem(AUTH_KEYS.REFRESH_TOKEN);

/**
 * Remove user data
 */
export const removeUser = () => removeItem(AUTH_KEYS.USER);

/**
 * Clear all authentication data
 */
export const clearAuth = () => clearAuthData();

/**
 * Check if user is authenticated (has a token)
 */
export const isAuthenticated = () => {
  const token = getToken();
  return Boolean(token);
};

// Export the keys for reference if needed
export const AUTH_STORAGE_KEYS = AUTH_KEYS;
